/**
 * The fuel module's paths.
 *
 * `/fuel` is the fleet dashboard, `/fuel/vehicles/:vehicleId` is one vehicle's tank,
 * and `/fuel/transactions` is the card ledger and its import. The per-vehicle screen
 * takes the range in the QUERY STRING rather than in the path, for the same reason
 * the trips module does: the URL a manager pastes into a ticket then carries the
 * whole question — this truck, these two days — and reproduces exactly.
 *
 * Route paths stay in English while the UI defaults to French. A localized URL set
 * would mean two routing tables and a redirect on every language switch; the address
 * bar is not a translated surface.
 *
 * Registration lives in `routes/app-routes.tsx`, which this phase does not own —
 * see NOTES-PHASE5-WEB.md §1 for the exact patch.
 */

export const FUEL_PATH = '/fuel'
export const VEHICLE_FUEL_PATH = '/fuel/vehicles/:vehicleId'
export const FUEL_TRANSACTIONS_PATH = '/fuel/transactions'
/**
 * The dated price series. Under `/fuel` rather than `/admin` deliberately: the person
 * who reads a cost per kilometre is the person who types the pump price it was computed
 * from, and burying it in administration is how a table nobody maintains ends up
 * silently feeding every cost figure in the product.
 */
export const FUEL_PRICES_PATH = '/fuel/prices'

export const FUEL_QUERY_KEYS = {
  from: 'from',
  to: 'to',
  /** `refill` / `drain`, or absent for both. */
  event: 'event',
  /** Filters the ledger down to the rows the tank disagreed with. */
  status: 'status',
} as const

export interface FuelRangeParams {
  /** `YYYY-MM-DD` day keys in the tenant's timezone, never instants. */
  from?: string
  to?: string
}

function withRange(base: string, params: FuelRangeParams, extra: Record<string, string> = {}) {
  const search = new URLSearchParams()
  if (params.from) search.set(FUEL_QUERY_KEYS.from, params.from)
  if (params.to) search.set(FUEL_QUERY_KEYS.to, params.to)
  for (const [key, value] of Object.entries(extra)) search.set(key, value)
  const query = search.toString()
  return query.length > 0 ? `${base}?${query}` : base
}

export function fuelDashboardPath(params: FuelRangeParams = {}): string {
  return withRange(FUEL_PATH, params)
}

export function vehicleFuelPath(vehicleId: string, params: FuelRangeParams = {}): string {
  return withRange(`/fuel/vehicles/${vehicleId}`, params)
}

export function fuelTransactionsPath(params: FuelRangeParams & { status?: string } = {}): string {
  const { status, ...range } = params
  return withRange(FUEL_TRANSACTIONS_PATH, range, status ? { status } : {})
}
